import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { forwardRef } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { SrsBallotService } from '../../services/srs-ballot.service';
import { SrsBallotReviewStage } from '../../models/sr/srs-ballot-review-stage';
import { Subscription } from 'rxjs';
import { BallotSearchService } from '../../services/ballot-search.service';
import { BallotType } from '../../../../../shared/enums/ballot-type-enum';
import { UnifiedBallotStatus } from '../../../../../shared/enums/unified-ballot-status-enum';
import { IConnectEnumValue, IUnifiedBallotStatusEnumValue } from '../../../../../shared/models/connect-enum';
import { IOption } from '../../../../controls/models/option';


@Component({
    selector: 'app-select-multiple-ballot-review-stage',
    templateUrl: './select-multiple-ballot-review-stage.component.html',
    styleUrl: './select-multiple-ballot-review-stage.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectMultipleBallotReviewStageComponent),
            multi: true
        }
    ],
    standalone: false
})
export class SelectMultipleBallotReviewStageComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() id: string = 'select-ballot-review-stage';
  @Input() label: string = 'Filter by Review Stage';
  @Input() selectedBallotStatuses: UnifiedBallotStatus[] = [];
  @Input() selectedBallotTypes: BallotType[] = [];
  @Output() viewLoaded = new EventEmitter<void>();

  onChange: any = () => {};
  onTouched: any = () => {};
  isDisabled: boolean = false;

  private _values: SrsBallotReviewStage[] = [];
  get values() {
    return this._values;
  }
  set values(newValue: SrsBallotReviewStage[]) {
    this._values = newValue;
    this.onChange(newValue);
  }

  options: IOption<SrsBallotReviewStage | null>[] = [];
  allSrsBallotReviewStages: IConnectEnumValue<SrsBallotReviewStage>[] = [];
  allUnifiedBallotStatusEnums: IUnifiedBallotStatusEnumValue[] = [];

  subsciptions: Subscription = new Subscription();

  constructor(
    private srsBallotService: SrsBallotService,
    private ballotSearchService: BallotSearchService
  ) { }


  ngOnDestroy(): void {
    this.subsciptions.unsubscribe();
  }

  ngOnInit(): void {
    this.subsciptions.add(this.srsBallotService.getSrsBallotReviewStageEnums().subscribe((response) => {
      if (response && response.content) {
        this.allSrsBallotReviewStages = response.content
        .filter(stage => stage.id !== SrsBallotReviewStage.ReadyForReviewSubmission);
        this.setSelectOptions();
      }
    }));

    this.subsciptions.add(this.ballotSearchService.getUnifiedBallotStatusEnumDto().subscribe((response) => {
      if (response && response.content) {
        this.allUnifiedBallotStatusEnums = response.content;
      }
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedBallotStatuses'] && !changes['selectedBallotStatuses'].firstChange) {
      this.setSelectOptions();
    }

    if (changes['selectedBallotTypes'] && !changes['selectedBallotTypes'].firstChange) {
      this.setSelectOptions();
    }
  }

  ngAfterViewInit(): void {
    this.viewLoaded.emit();
  }

  writeValue(obj: any): void {
    this.values = obj ? obj : [];
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  setSelectOptions(): void {
    this.options = [];
    this.options.push({ value: null, label: 'None' });

    if (!this.areDependenciesReady()) {
      this.options.push(...this.getAllReviewStageOptions());
      this.filterInvalidValues();
      return;
    }

    if (this.isSRTypeSelected()) {
      if (this.selectedBallotStatuses.length === 0) {
        this.options.push(...this.getAllReviewStageOptions());
      } else {
        this.options.push(...this.getFilteredReviewStageOptions());
      }
    }

    this.filterInvalidValues();
  }

  private areDependenciesReady(): boolean {
    return (
      this.selectedBallotTypes !== undefined &&
      this.selectedBallotStatuses !== undefined &&
      this.allSrsBallotReviewStages.length > 0 &&
      this.allUnifiedBallotStatusEnums.length > 0
    );
  }

  private isSRTypeSelected(): boolean {
    return (
      this.selectedBallotTypes.length === 0 ||
      this.selectedBallotTypes.includes(BallotType.SR)
    );
  }

  private getAllReviewStageOptions(): IOption<SrsBallotReviewStage>[] {
    return this.allSrsBallotReviewStages.map(stage => ({
      value: stage.id,
      label: stage.displayText
    }));
  }

  private getFilteredReviewStageOptions(): IOption<SrsBallotReviewStage>[] {
    const relevantReviewStages: SrsBallotReviewStage[] = this.selectedBallotStatuses
      .flatMap(status => {
        const ballotStatusEnum = this.allUnifiedBallotStatusEnums.find(enumDto => enumDto.id === status);
        return ballotStatusEnum?.srsBallotReviewStageMapping ?? [];
      })
      .filter(stage => stage !== undefined && stage !== null);

    const uniqueReviewStages: SrsBallotReviewStage[] = Array.from(new Set(relevantReviewStages));
    return uniqueReviewStages
      .map(stageId => {
        const stage = this.allSrsBallotReviewStages.find(e => e.id === stageId);
        return stage ? { value: stage.id, label: stage.displayText } : null;
      })
      .filter((o): o is IOption<SrsBallotReviewStage> => !!o);
  }

  private filterInvalidValues(): void {
    const validValues = this.options.map(y => y.value);
    this.values = [...this.values].filter(x => validValues.includes(x));
  }
}
